/* eslint-disable @angular-eslint/no-output-on-prefix */
/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
import {
  faGear,
  faCircleCheck,
  faQuestionCircle,
  faCheck,
  faEdit,
  faKey,
  faTriangleExclamation,
  faCircleExclamation,
  faXmark,
  faLocationDot,
} from '@fortawesome/pro-solid-svg-icons';
import {
  IRobotWordTableColumn,
  IRobotWordTableKeyVerticalAlignEnum,
  TDocumentSentencesAndWordClicks,
} from 'src/modules/shared/models/interfaces/robots.interface';
import { Component, DoCheck, EventEmitter, Input, IterableDiffer, IterableDiffers, OnChanges, Output, SimpleChanges } from '@angular/core';
import { faDrawSquare } from '@fortawesome/pro-duotone-svg-icons';
import { faArrowPointer, faCircleNotch } from '@fortawesome/pro-light-svg-icons';
import { faPlus, faTable, faThumbTack, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faCircleDashed, faCircleX, faKey as faKeyThin } from '@fortawesome/pro-thin-svg-icons';
import { IFilePageAndWords, IFileTable, TPrimitiveType } from 'src/modules/shared/models/interfaces/files.interface';
import { Calcs } from 'src/modules/shared/utilities/calculations';
import { IGroupConfigurationKey } from 'src/modules/shared/models/interfaces/group-keys.interface';
import { wordDataTypeDisplay, WordDataTypeEnum } from 'src/modules/shared/models/enums/word-data-type.enum';
import { CurrencyValuePipe } from 'src/modules/shared/pipes/currencyValueFilter.pipe';
import { DateValuePipe } from 'src/modules/shared/pipes/dateValueFilter.pipe';
import { GeneratorsUtility } from 'src/modules/shared/utilities/generators';
import { GroupConfiguration, GroupConfigurationKey } from 'src/modules/shared/models/group-keys.model';
import { ISmartCanvasOptions } from 'src/modules/shared/models/interfaces/smart-canvas.interface';
import { EventEmitterService } from 'src/modules/shared/services/event-emitter.service';
import { RobotPreview, RobotSentence, RobotWordClick, RobotWordClickXPath } from 'src/modules/shared/models/robots.model';
import { ClassificationParameterTypeEnum } from 'src/modules/shared/models/interfaces/classification-parameters.interface';
import { ILandmark, LandmarkPositionEnum } from 'src/modules/shared/models/interfaces/landmarks.interface';
import { SentenceTypeEnum } from 'src/modules/shared/models/enums/sentence-type.enum';

/**
 * @todo Passar todo o código de Landmarks para um componente separado
 * @todo Passar todo o código de Tabela para um componente separado
 */
@Component({
  selector: 'hcr-robot-creation-word-input',
  templateUrl: './robot-creation-word-input.component.html',
  styleUrls: ['./robot-creation-word-input.component.sass'],
})
export class RobotCreationWordInputComponent implements OnChanges, DoCheck {
  @Input() selectedGroupConfiguration: GroupConfiguration;
  @Input() configurationKey: IGroupConfigurationKey;
  @Input() selectedFilePagesAndWords: IFilePageAndWords[];
  @Input() pinnedValue: any;
  @Input() selectedDocumentSentencesAndWordClicks: TDocumentSentencesAndWordClicks;
  @Input() smartCanvasOptions: ISmartCanvasOptions;
  @Input() selectedRobotFilePreview!: RobotPreview;
  @Input() loadingPreview!: boolean;
  @Input() wordClicks: RobotWordClick[];
  @Output() onPinKeyValue: EventEmitter<{ pinned: boolean; key: IGroupConfigurationKey; value: string }> = new EventEmitter();
  @Output() onClear: EventEmitter<IGroupConfigurationKey> = new EventEmitter();
  @Output() onRemoveCustomInput: EventEmitter<IGroupConfigurationKey> = new EventEmitter();
  @Output() onChange: EventEmitter<void> = new EventEmitter();
  @Output() onChangeSliderValue: EventEmitter<{ value: number; selectedWord: RobotWordClick }> = new EventEmitter();
  @Output() onHighlight: EventEmitter<GroupConfigurationKey> = new EventEmitter();
  @Output() onUnhighlight: EventEmitter<GroupConfigurationKey> = new EventEmitter();

  public wordDataTypeEnum = WordDataTypeEnum;

  public landmarkStates = {
    opened: false,
    addingLandmark: false,
  };

  public inputTypes = Object.keys(<object>wordDataTypeDisplay).map((key) => {
    return { key: key, value: wordDataTypeDisplay[key] };
  });

  /**
   * @todo Validar colunas obrigatórias da configuração de tabela
   */

  public get thresholdValue(): number {
    return this.selectedWord.extractionThreshold;
  }

  public get isValid(): boolean {
    return !this.errorMessage && this.getWordInputValue() ? true : false;
  }

  public get isFilled(): boolean {
    return (this.states.pinned === true && this.pinnedValue !== null) || this.selectedWord ? true : false;
  }

  public get getNumberOfTypeOptions(): number {
    let count = 0;
    for (const key in WordDataTypeEnum) {
      if (typeof WordDataTypeEnum[key] === 'number') {
        const value = +WordDataTypeEnum[key];
        if ((value & this.configurationKey.type) === value) {
          count++;
        }
      }
    }
    return count;
  }

  public get selectedWord(): RobotWordClick | null {
    return this.wordClicks.find((word) => word.word.key === this.configurationKey.key);
  }

  public get isValidCustomInput(): boolean {
    if (this.configurationKey.isCustom) {
      return (
        this.configurationKey.label &&
        this.configurationKey.label.length <= this.minCustomInputLength &&
        this.configurationKey.key &&
        this.configurationKey.type !== null
      );
    }

    return true;
  }

  public get isValidTable(): boolean {
    if (this.configurationKey.type === WordDataTypeEnum.table) {
      return this.selectedWord?.tableConfiguration?.keyColumn.columnIndex !== null;
    }

    return true;
  }

  public get isXpath(): boolean {
    return this.selectedGroupConfiguration?.type === ClassificationParameterTypeEnum.xPath;
  }

  public states: { [key: string]: boolean } = {
    pinned: false,
    configurationOpened: false,
    addingLandmark: false,
    editingPinnedValue: false,
    highlighted: false,
  };

  public icons = {
    circleCheck: faCircleCheck,
    circleDashed: faCircleDashed,
    circleExclamation: faCircleExclamation,
    circleX: faCircleX,
    gear: faGear,
    arrowPointer: faArrowPointer,
    drawSquare: faDrawSquare,
    thumbtack: faThumbTack,
    locationDot: faLocationDot,
    questionCircle: faQuestionCircle,
    check: faCheck,
    edit: faEdit,
    trash: faTrash,
    plus: faPlus,
    table: faTable,
    key: faKey,
    keyThin: faKeyThin,
    triangleExclamation: faTriangleExclamation,
    xMark: faXmark,
    spinner: faCircleNotch,
  };

  public oldPinnedValue = '';
  public errorMessage = null;
  public minCustomInputLength = 3;
  public classificationTypes = ClassificationParameterTypeEnum;

  private _iterableDiffer: IterableDiffer<RobotWordClick>;

  constructor(private _iterableDiffers: IterableDiffers, private _eventEmitterService: EventEmitterService) {
    this._iterableDiffer = this._iterableDiffers.find([]).create(null);
  }

  public previewWordClicksOnSliderChanges(value: number): void {
    this.selectedWord.landmarks?.forEach((landmark) => (landmark.sentence.identificationThreshold = value));
    this.onChangeSliderValue.emit({
      value,
      selectedWord: this.selectedWord,
    });
  }

  public dataTypeValueChange(value: number): void {
    this.selectedWord.word.dataType = value;
    this.onChange.emit();
  }

  public addLandmark(): void {
    this.selectedWord.landmarks.push({
      position: LandmarkPositionEnum.rightFromLine,
      sentence: new RobotSentence({
        text: '',
        polygon: this.selectedWord.polygon,
        identificationThreshold: this.selectedWord.extractionThreshold,
        type: SentenceTypeEnum.Default,
        isDenied: false,
      }),
    });
  }

  public changeThreshold(value: number): void {
    this.selectedWord.extractionThreshold = value;
    this.selectedWord.landmarks?.forEach((landmark) => (landmark.sentence.identificationThreshold = value));
    this.onChange.emit();
  }

  public isValidPinnedValue(): boolean {
    if (this.states.pinned) {
      return this.pinnedValue !== null;
    }

    return true;
  }

  public toggleConfiguration(): void {
    this.states.configurationOpened = !this.states.configurationOpened;
  }

  public toggleLandkmarkAdding(): void {
    this.states.addingLandmark = !this.states.addingLandmark;
  }

  public openPinnedValue(): void {
    this.states.pinned = true;
    this.states.editingPinnedValue = true;
    this.oldPinnedValue = this.pinnedValue;
  }

  public closePinnedValue(): void {
    this.pinnedValue = this.oldPinnedValue;
    this.states.editingPinnedValue = false;
    this.states.configurationOpened = false;
    this.states.pinned = this.pinnedValue !== null;
  }

  public savePinnedValue(): void {
    this.oldPinnedValue = this.pinnedValue;
    this.states.configurationOpened = false;
    this.states.editingPinnedValue = false;
    this.states.pinned = this.pinnedValue !== null;
    this.onPinKeyValue.emit({ pinned: this.states.pinned, key: this.configurationKey, value: this.pinnedValue });
    this.onChange.emit();
  }

  public removeLandmark(index: number): void {
    this.selectedWord.landmarks.splice(index, 1);
    this.onChange.emit();
  }

  public updateWordClickLandmark(data: ILandmark, index: number): void {
    Object.assign(this.selectedWord.landmarks[index], data);
    this.onChange.emit();
  }

  public clear(): void {
    this.pinnedValue = null;
    this.states.pinned = false;
    this.onClear.emit(this.configurationKey);
    this.onChange.emit();
    this.states.configurationOpened = false;
  }

  public getTable(): IFileTable | null {
    const pageNumber = this.selectedWord._pageNumber || 1; /// TODO: usar pageNumber quando ele existir na API
    const page = this.selectedFilePagesAndWords[pageNumber - 1];

    if (page && page.tables?.length) {
      return Calcs.getClosestTableFromCentroid(page.tables, this.selectedWord.polygon.centroid);
    }

    return null;
  }

  public getTableColumnName(columnIndex: number): string | null {
    const table = this.getTable();

    if (table) {
      return table.cells.filter((cell) => cell.columnIndex === columnIndex && cell.isHeader)[0].text;
    } else {
      return null;
    }
  }

  public getTableColumnValue(columnKey: string): IRobotWordTableColumn | null {
    return this.selectedWord.tableConfiguration.columns.find((column) => column.key === columnKey);
  }

  public updateTableColumnValue(value: IRobotWordTableColumn): void {
    const column = this.selectedWord.tableConfiguration.columns.findIndex((column) => column.key === value.key);
    if (column !== -1) {
      this.selectedWord.tableConfiguration.columns[column] = value;
    } else {
      this.selectedWord.tableConfiguration.columns.push(value);
    }
  }

  public getWordInputValue(): TPrimitiveType {
    let value: TPrimitiveType;
    if (this.selectedRobotFilePreview) {
      const page = this.selectedRobotFilePreview.pages.find((page) => page.allSentencesTestsPassed);
      if (page) {
        value = page.wordClicks.find((wc) => wc.key === this.configurationKey.key)?.valueActual;
      }
    }

    if (value !== null && value !== undefined) {
      switch (this.configurationKey.type) {
        case WordDataTypeEnum.currency:
          return new CurrencyValuePipe().transform(<number>value) || value;
        case WordDataTypeEnum.dateTime:
          return new DateValuePipe().transform(<Date>value) || value;
        case WordDataTypeEnum.table:
          return 'Tabela selecionada';
        default:
          return value;
      }
    } else {
      if (this.isValidPinnedValue()) {
        return typeof this.pinnedValue === 'boolean' ? (this.pinnedValue ? 'Sim' : 'Não') : this.pinnedValue || value;
      } else {
        return value;
      }
    }
  }

  public onChangeKeyLabel(label: string): void {
    const generatedKey = GeneratorsUtility.generateIdFromString(label);
    if (!label) {
      this.errorMessage = 'O nome do campo não pode estar vazio.';
      return;
    } else if (label.length <= this.minCustomInputLength) {
      this.errorMessage = `O nome do campo precisa ter no mínimo ${this.minCustomInputLength} caracteres.`;
      return;
    } else if (this.selectedGroupConfiguration.keys.find((key) => key.key?.toLowerCase() === generatedKey.toLowerCase())) {
      this.errorMessage = 'Este campo já existe.';
      return;
    }

    if (this.wordClicks[this.configurationKey.key]) {
      delete Object.assign(this.wordClicks, { [generatedKey]: this.wordClicks[this.configurationKey.key] })[this.configurationKey.key];
      this.selectedWord.word.key = generatedKey;
    }
    this.errorMessage = null;
    this.configurationKey.key = generatedKey;
  }

  public removeCustomInput(): void {
    this.onRemoveCustomInput.emit(this.configurationKey);
  }

  public onChangeXPath(xpath: string): void {
    if (!this.selectedWord) {
      this.wordClicks.push(new RobotWordClickXPath(xpath, this.configurationKey));
    } else {
      this.selectedWord.xPath = xpath;
    }
  }

  public highlightWordClick(): void {
    this.states.highlighted = true;
    this.onHighlight.emit(this.configurationKey);
  }

  public unhighlightWordClick(): void {
    this.states.highlighted = false;
    this.onUnhighlight.emit(this.configurationKey);
  }

  ngDoCheck(): void {
    const hasChanged = this._iterableDiffer.diff(this.wordClicks);

    if (hasChanged && this.selectedWord) {
      if (this.configurationKey.type === WordDataTypeEnum.table && !this.selectedWord.tableConfiguration) {
        this.selectedWord.tableConfiguration = {
          keyColumn: {
            columnIndex: null,
            key: this.configurationKey.tableConfiguration.keyColumn.key,
            dataType: this.configurationKey.tableConfiguration.keyColumn.type,
            verticalAlign: IRobotWordTableKeyVerticalAlignEnum.top,
          },
          columns: [],
        };
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pinnedValue) {
      this.states.pinned = this.pinnedValue ? true : false;
    }
  }
}
